import uuidv4 from "uuid/v4";

import { streamStore as store } from "~/stream/utils";
import { __globalModalContext } from "~/ui/modals";

export const INVITE_BANNER_KEY = "inviteBanner";
export const INVITE_EXTERNAL_BANNER_KEY = "inviteExternalBanner";
export const SET_MANAGER_BANNER_KEY = "suggestedManager";
export const RECURRING_1ON1_BANNER_KEY = "recurring1on1";
export const BANNER_KEYS = [
	INVITE_BANNER_KEY,
	INVITE_EXTERNAL_BANNER_KEY,
	SET_MANAGER_BANNER_KEY,
	RECURRING_1ON1_BANNER_KEY,
] as const;

export const bannersAnalyticsProperties = {
	fingerprint: uuidv4(),
};

export type BannerType = (typeof BANNER_KEYS)[number];

type BannerDismissedType = {
	key: string;
	dismissedUntil: string;
};

/**
 * Determines if a Banner has been marked as dismissed based on a unique BANNER_KEY and identifier combination
 * @param BANNER_KEY - INVITE_BANNER_KEY | SET_MANAGER_BANNER_KEY | RECURRING_1ON1_BANNER_KEY
 * @param identifier - streamId or userId
 * @returns boolean
 */
export function isBannerDismissed(BANNER_KEY: BannerType, identifier: string) {
	const key = `${BANNER_KEY}_${identifier}`;

	const allDismissedBanners = (store.get(BANNER_KEY) ?? []) as BannerDismissedType[];
	if (BANNER_KEY === SET_MANAGER_BANNER_KEY && allDismissedBanners.length >= 3) return true;

	const dismissedItem = allDismissedBanners.find((item: BannerDismissedType) => item.key === key);
	if (!dismissedItem) {
		return false;
	}

	return new Date(dismissedItem.dismissedUntil) >= new Date();
}

/**
 * Marks a Banner as dismissed based on a unique BANNER_KEY and identifier combination.
 * @param BANNER_KEY - INVITE_BANNER_KEY | SET_MANAGER_BANNER_KEY | RECURRING_1ON1_BANNER_KEY
 * @param identifier - streamId or userId
 */
export function dismissBanner(BANNER_KEY: BannerType, identifier: string, permanent = false) {
	const key = `${BANNER_KEY}_${identifier}`;

	const allDismissedBanners = (store.get(BANNER_KEY) ?? []) as BannerDismissedType[];
	const dismissedItemIndex = allDismissedBanners.findIndex((item: BannerDismissedType) => item.key === key);

	// This banner was already dismissed once, so set dismissedUntil to
	// an arbitrarily far date to prevent it from showing again
	if (dismissedItemIndex > -1) {
		allDismissedBanners[dismissedItemIndex].dismissedUntil = "2099-01-01";
		store.set(BANNER_KEY, allDismissedBanners);
	} else {
		// When dismissing a banner for the FIRST time it should show again after 7 days
		// Unless we expclicitly want to dismiss it permanently
		const dismissedUntil = permanent ? new Date("2099-01-01") : new Date();
		dismissedUntil.setDate(dismissedUntil.getDate() + 7);

		allDismissedBanners.push({
			key,
			dismissedUntil: dismissedUntil.toISOString(),
		});

		store.set(BANNER_KEY, allDismissedBanners);
	}
}
